import { useCallback, useEffect, useMemo, useState } from "react";

import { History, Upload, Trash2, Search, FileSpreadsheet, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import BackButton from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import ExcelJS from "exceljs";
import { loadCompanyMasters, type CompanyMaster } from "@/lib/supplierMaster";
import { loadOrganizations, type Organization } from "@/lib/organizations";
import { useAuth } from "@/contexts/AuthContext";
import { formatNumber, parseNumericLike } from "@/lib/numberFormatting";
import {
  countHistory,
  deleteHistoryEntry,
  insertHistoryEntries,
  listHistory,
  type InvoiceHistoryEntry,
  type InvoiceHistoryInput,
} from "@/lib/invoiceHistory";

type ColumnKey = "supplier_tax_id" | "supplier_name" | "invoice_number" | "invoice_date" | "base_amount" | "vat_amount" | "total_amount" | "skip";

interface ParsedRow {
  raw: any[];
  mapped: Partial<InvoiceHistoryInput> & { _row: number };
  errors: string[];
}

const REQUIRED: ColumnKey[] = ["supplier_tax_id"];

const COLUMN_LABELS: Record<ColumnKey, string> = {
  supplier_tax_id: "NIF proveedor *",
  supplier_name: "Nombre proveedor",
  invoice_number: "Nº factura (recomendado)",
  invoice_date: "Fecha (opcional)",
  base_amount: "Base imponible (recomendado)",
  vat_amount: "Cuota IVA",
  total_amount: "Total (se calcula si falta)",
  skip: "— Ignorar —",
};

const guessColumn = (header: string): ColumnKey => {
  const h = (header || "").toLowerCase().trim().replace(/[.\s]+/g, " ");
  if (/(n\s*i\s*f|c\s*i\s*f|nif|cif|tax)/.test(h)) return "supplier_tax_id";
  if (/(proveedor|supplier|nombre|emisor|expedidor|raz[oó]n)/.test(h)) return "supplier_name";
  if (/(n[ºo°]?\s*fact|num\s*fact|invoice\s*number|n\s*fac|nº?\s*factura|num\s*fra|n\s*fra)/.test(h)) return "invoice_number";
  if (/(fecha|date|f\s*emis)/.test(h)) return "invoice_date";
  if (/(base|imponible)/.test(h)) return "base_amount";
  // "cuota" o "iva" como importe (no como porcentaje "% iva")
  if (/(cuota|cuota\s*iva|iva\s*€|iva\s*eur)/.test(h)) return "vat_amount";
  if (/^iva$/.test(h) || (/iva/.test(h) && !/%/.test(h) && !/tipo/.test(h))) return "vat_amount";
  if (/(total|importe(?!\s*neto)|total\s*factura)/.test(h)) return "total_amount";
  return "skip";
};

const parseExcelDate = (value: any): string | null => {
  if (!value) return null;
  if (value instanceof Date) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const s = String(value).trim();
  // ISO YYYY-MM-DD
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`;
  // DD/MM/YYYY o DD-MM-YYYY
  m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/);
  if (m) {
    const d = m[1].padStart(2, "0");
    const mo = m[2].padStart(2, "0");
    let y = m[3];
    if (y.length === 2) y = (Number(y) > 50 ? "19" : "20") + y;
    return `${y}-${mo}-${d}`;
  }
  return null;
};

const HistoricoFacturas = () => {
  const { toast } = useToast();
  const { isAdmin } = useAuth();
  const [companies, setCompanies] = useState<CompanyMaster[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [companyId, setCompanyId] = useState<string>("");
  const [count, setCount] = useState<number>(0);
  const [list, setList] = useState<InvoiceHistoryEntry[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // Estado del importador
  const [importHeaders, setImportHeaders] = useState<string[]>([]);
  const [importRows, setImportRows] = useState<any[][]>([]);
  const [mapping, setMapping] = useState<ColumnKey[]>([]);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    void Promise.all([
      loadCompanyMasters(),
      isAdmin ? loadOrganizations() : Promise.resolve([] as Organization[]),
    ]).then(([cs, orgs]) => {
      setCompanies(cs);
      setOrganizations(orgs);
      if (isAdmin && orgs.length > 0) {
        const stored = localStorage.getItem("admin.lastOrgId");
        const valid = stored && orgs.some((o) => o.id === stored) ? stored : "";
        setSelectedOrgId(valid);
        // Si hay asesoría persistida, autoselecciona primera empresa de esa asesoría
        if (valid) {
          const first = cs.find((c) => c.organization_id === valid);
          if (first) setCompanyId(first.id);
        }
      } else if (!isAdmin && cs.length > 0) {
        setCompanyId(cs[0].id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const showOrgFilter = isAdmin && organizations.length > 1;

  const visibleCompanies = useMemo(() => {
    if (!showOrgFilter) return companies;
    return companies.filter((c) => c.organization_id === selectedOrgId);
  }, [companies, showOrgFilter, selectedOrgId]);

  const handleOrgChange = (orgId: string) => {
    setSelectedOrgId(orgId);
    localStorage.setItem("admin.lastOrgId", orgId);
    setCompanyId("");
    setCount(0);
    setList([]);
  };

  const refresh = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    const [c, l] = await Promise.all([countHistory(companyId), listHistory(companyId, search)]);
    setCount(c);
    setList(l);
    setLoading(false);
  }, [companyId, search]);

  useEffect(() => { void refresh(); }, [refresh]);

  const handleFile = async (file: File) => {
    try {
      const buf = await file.arrayBuffer();
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(buf);
      const ws = wb.worksheets[0];
      if (!ws) throw new Error("Excel vacío");
      const rows: any[][] = [];
      ws.eachRow((row) => {
        const arr: any[] = [];
        row.eachCell({ includeEmpty: true }, (cell) => {
          arr.push(cell.value);
        });
        rows.push(arr);
      });
      if (rows.length < 2) throw new Error("Necesita al menos cabecera y una fila de datos");
      const headers = rows[0].map((v) => String(v ?? "").trim());
      setImportHeaders(headers);
      setImportRows(rows.slice(1));
      setMapping(headers.map((h) => guessColumn(h)));
    } catch (e: any) {
      toast({ title: "No se pudo leer el Excel", description: e?.message ?? "Error inesperado", variant: "destructive" });
    }
  };

  const parsedPreview: ParsedRow[] = useMemo(() => {
    if (importRows.length === 0 || mapping.length === 0 || !companyId) return [];
    return importRows.slice(0, 100).map((row, idx) => {
      const out: Partial<InvoiceHistoryInput> & { _row: number } = { _row: idx + 2 };
      const errors: string[] = [];
      mapping.forEach((key, ci) => {
        if (key === "skip") return;
        const raw = row[ci];
        if (key === "invoice_date") {
          if (raw === null || raw === undefined || raw === "") return;
          const d = parseExcelDate(raw);
          if (!d) errors.push("Fecha con formato no reconocido");
          else out.invoice_date = d;
        } else if (key === "base_amount" || key === "vat_amount" || key === "total_amount") {
          if (raw !== null && raw !== undefined && raw !== "") {
            const n = parseNumericLike(raw);
            if (!Number.isFinite(n)) errors.push(`${COLUMN_LABELS[key]} no numérico`);
            else (out as any)[key] = n;
          }
        } else if (key === "supplier_tax_id") {
          const s = String(raw ?? "").trim();
          if (!s) errors.push("NIF vacío");
          else out.supplier_tax_id = s;
        } else {
          out[key as "supplier_name" | "invoice_number"] = String(raw ?? "").trim();
        }
      });
      // Calcular total derivado para la vista previa
      if (!Number.isFinite(out.total_amount as number)) {
        if (Number.isFinite(out.base_amount as number) && Number.isFinite(out.vat_amount as number)) {
          out.total_amount = Math.round((Number(out.base_amount) + Number(out.vat_amount)) * 100) / 100;
        } else if (Number.isFinite(out.base_amount as number)) {
          out.total_amount = Number(out.base_amount);
        } else {
          errors.push("Sin Total ni Base imponible");
        }
      }
      return { raw: row, mapped: out, errors };
    });
  }, [importRows, mapping, companyId]);

  const fullValidationStats = useMemo(() => {
    const requiredMapped = REQUIRED.every((k) => mapping.includes(k));
    return { requiredMapped };
  }, [mapping]);

  const handleImport = async () => {
    if (!companyId) return;
    if (!fullValidationStats.requiredMapped) {
      toast({ title: "Falta el NIF", description: "Mapea al menos la columna NIF.", variant: "destructive" });
      return;
    }
    setImporting(true);
    const entries: InvoiceHistoryInput[] = [];
    const errs: string[] = [];

    importRows.forEach((row, idx) => {
      const e: any = {};
      mapping.forEach((key, ci) => {
        if (key === "skip") return;
        const raw = row[ci];
        if (key === "invoice_date") {
          if (raw === null || raw === undefined || raw === "") return;
          const d = parseExcelDate(raw);
          if (d) e.invoice_date = d;
        } else if (key === "base_amount" || key === "vat_amount" || key === "total_amount") {
          if (raw !== null && raw !== undefined && raw !== "") {
            const n = parseNumericLike(raw);
            if (Number.isFinite(n)) e[key] = n;
          }
        } else if (key === "supplier_tax_id") {
          e.supplier_tax_id = String(raw ?? "").trim();
        } else {
          e[key] = String(raw ?? "").trim();
        }
      });

      // Si no hay total pero tenemos base + cuota, lo calculamos
      if (!Number.isFinite(e.total_amount) && Number.isFinite(e.base_amount) && Number.isFinite(e.vat_amount)) {
        e.total_amount = Math.round((Number(e.base_amount) + Number(e.vat_amount)) * 100) / 100;
      }
      // Si solo hay base (sin IVA ni total), usamos la base como total para no perder la fila
      if (!Number.isFinite(e.total_amount) && Number.isFinite(e.base_amount)) {
        e.total_amount = Number(e.base_amount);
      }

      if (!e.supplier_tax_id) {
        errs.push(`Fila ${idx + 2}: NIF vacío`);
        return;
      }
      if (!Number.isFinite(e.total_amount)) {
        errs.push(`Fila ${idx + 2}: sin Total ni Base imponible`);
        return;
      }
      entries.push({
        company_id: companyId,
        supplier_tax_id: e.supplier_tax_id,
        supplier_name: e.supplier_name ?? "",
        invoice_number: e.invoice_number || null,
        invoice_date: e.invoice_date ?? null,
        base_amount: e.base_amount ?? null,
        vat_amount: e.vat_amount ?? null,
        total_amount: e.total_amount,
        source: "excel_import",
      });
    });

    const res = await insertHistoryEntries(entries);
    setImporting(false);

    toast({
      title: `Importación finalizada`,
      description: `${res.inserted} insertadas · ${res.skipped} duplicadas (omitidas) · ${errs.length + res.errors.length} errores`,
    });

    setImportHeaders([]);
    setImportRows([]);
    setMapping([]);
    void refresh();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("¿Eliminar esta entrada del histórico?")) return;
    const ok = await deleteHistoryEntry(id);
    if (ok) {
      toast({ title: "Entrada eliminada" });
      void refresh();
    } else {
      toast({ title: "No se pudo eliminar", variant: "destructive" });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-primary">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-6 py-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--brand-green))]">
            <History className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-base font-bold text-primary-foreground">Histórico de facturas</h1>
            <p className="text-xs text-primary-foreground/60">Control permanente de duplicados</p>
          </div>
          <BackButton className="text-xs" />
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8 space-y-6">
        {/* Selector asesoría + empresa */}
        <div className="flex flex-wrap items-end gap-4">
          {showOrgFilter && (
            <div className="min-w-[220px]">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Asesoría</Label>
              <Select value={selectedOrgId} onValueChange={handleOrgChange}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Selecciona asesoría" /></SelectTrigger>
                <SelectContent>
                  {organizations.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="min-w-[260px]">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Empresa</Label>
            <Select
              value={companyId}
              onValueChange={setCompanyId}
              disabled={showOrgFilter && !selectedOrgId}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={showOrgFilter && !selectedOrgId ? "Selecciona primero asesoría" : "Selecciona empresa"} />
              </SelectTrigger>
              <SelectContent>
                {visibleCompanies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {companyId && (
            <Badge variant="secondary" className="h-9 text-sm">
              {count.toLocaleString("es-ES")} facturas en histórico
            </Badge>
          )}
        </div>

        {showOrgFilter && !selectedOrgId && (
          <p className="text-sm text-muted-foreground">
            Selecciona una asesoría para empezar.
          </p>
        )}

        {/* Importador */}
        <div className="rounded-lg border bg-card p-5">
          <div className="mb-3 flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-semibold">Importar histórico desde Excel</h2>
          </div>

          {importHeaders.length === 0 ? (
            <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-muted py-8 text-center hover:border-primary/50">
              <Upload className="h-6 w-6 text-muted-foreground" />
              <span className="text-sm font-medium">Selecciona un archivo Excel (.xlsx)</span>
              <span className="text-xs text-muted-foreground">La primera fila debe contener los nombres de las columnas</span>
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f); e.currentTarget.value = ""; }}
              />
            </label>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                {importHeaders.map((h, i) => (
                  <div key={i} className="space-y-1">
                    <Label className="text-xs">
                      Columna: <span className="font-mono">{h || `(col ${i + 1})`}</span>
                    </Label>
                    <Select
                      value={mapping[i] ?? "skip"}
                      onValueChange={(v) => setMapping((prev) => prev.map((x, idx) => idx === i ? (v as ColumnKey) : x))}
                    >
                      <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(Object.keys(COLUMN_LABELS) as ColumnKey[]).map((k) => (
                          <SelectItem key={k} value={k}>{COLUMN_LABELS[k]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              <div className="rounded border bg-muted/30 p-3 text-xs">
                <p className="mb-2 font-medium">Vista previa (primeras {parsedPreview.length} filas)</p>
                <div className="max-h-64 overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-background">
                      <tr>
                        <th className="border px-2 py-1 text-left">Fila</th>
                        <th className="border px-2 py-1 text-left">NIF</th>
                        <th className="border px-2 py-1 text-left">Proveedor</th>
                        <th className="border px-2 py-1 text-left">Fecha</th>
                        <th className="border px-2 py-1 text-left">Nº factura</th>
                        <th className="border px-2 py-1 text-right">Base</th>
                        <th className="border px-2 py-1 text-right">Total</th>
                        <th className="border px-2 py-1 text-left">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedPreview.map((p) => (
                        <tr key={p.mapped._row} className={p.errors.length > 0 ? "bg-orange-50 dark:bg-orange-950/20" : ""}>
                          <td className="border px-2 py-1">{p.mapped._row}</td>
                          <td className="border px-2 py-1 font-mono">{p.mapped.supplier_tax_id ?? "—"}</td>
                          <td className="border px-2 py-1">{p.mapped.supplier_name ?? "—"}</td>
                          <td className="border px-2 py-1">{p.mapped.invoice_date ?? "—"}</td>
                          <td className="border px-2 py-1">{p.mapped.invoice_number ?? "—"}</td>
                          <td className="border px-2 py-1 text-right">{Number.isFinite(p.mapped.base_amount as number) ? formatNumber(p.mapped.base_amount) : "—"}</td>
                          <td className="border px-2 py-1 text-right">{Number.isFinite(p.mapped.total_amount) ? formatNumber(p.mapped.total_amount) : "—"}</td>
                          <td className="border px-2 py-1">
                            {p.errors.length === 0
                              ? <span className="inline-flex items-center gap-1 text-green-600"><CheckCircle2 className="h-3 w-3" /> OK</span>
                              : <span className="inline-flex items-center gap-1 text-orange-600"><AlertCircle className="h-3 w-3" /> {p.errors.join(", ")}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  {importRows.length} filas detectadas. Se omitirán las que ya existan (mismo NIF + Nº factura).
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setImportHeaders([]); setImportRows([]); setMapping([]); }}>
                    Cancelar
                  </Button>
                  <Button size="sm" onClick={handleImport} disabled={importing || !fullValidationStats.requiredMapped}>
                    {importing ? <><Loader2 className="mr-2 h-3 w-3 animate-spin" />Importando…</> : `Importar ${importRows.length} filas`}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Listado */}
        <div className="rounded-lg border bg-card p-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold">Histórico actual</h2>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por NIF, proveedor o nº factura"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 w-72 pl-7 text-xs"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>NIF</TableHead>
                  <TableHead>Proveedor</TableHead>
                  <TableHead>Nº factura</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Origen</TableHead>
                  <TableHead title="Fecha en la que la entrada se registró/generó en el histórico">Registrado</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">Cargando…</TableCell></TableRow>
                ) : list.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">Sin entradas en el histórico</TableCell></TableRow>
                ) : (
                  list.map((r) => {
                    const createdLabel = r.created_at
                      ? new Intl.DateTimeFormat("es-ES", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        }).format(new Date(r.created_at))
                      : "—";
                    return (
                      <TableRow key={r.id}>
                        <TableCell className="text-xs">{r.invoice_date}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {r.supplier_tax_id}
                          {r.extracted_tax_id && r.extracted_tax_id !== r.supplier_tax_id && (
                            <span className="block text-[10px] text-muted-foreground">real: {r.extracted_tax_id}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">{r.supplier_name || "—"}</TableCell>
                        <TableCell className="text-xs">{r.invoice_number || <span className="text-muted-foreground">(sin nº)</span>}</TableCell>
                        <TableCell className="text-right text-xs">{formatNumber(Number(r.total_amount))} €</TableCell>
                        <TableCell><Badge variant="outline" className="text-[10px]">{r.source === "excel_import" ? "Excel" : r.source === "vat_export" ? "Libro IVA" : "Manual"}</Badge></TableCell>
                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{createdLabel}</TableCell>
                        <TableCell>
                          <button onClick={() => handleDelete(r.id)} className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" title="Eliminar">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          {list.length >= 500 && (
            <p className="mt-2 text-xs text-muted-foreground">Mostrando los 500 más recientes. Usa el buscador para encontrar otros.</p>
          )}
        </div>
      </main>
    </div>
  );
};

export default HistoricoFacturas;
